import apiClient from './apiClient';

export type TimesheetStatus = 'draft' | 'submitted' | 'approved' | 'returned';
export type TimesheetHourType = 'regular' | 'overtime';

export interface Worker {
  id: string;
  name: string;
  badgeCode: string | null;
  teamId: string | null;
  team?: {
    id: string;
    name: string;
    teamType?: string;
  } | null;
  trade: string | null;
  isActive: boolean;
}

export interface TimesheetEntry {
  id: string;
  timesheetId: string;
  workerId: string;
  taskId: string | null;
  hours: number;
  hourType: TimesheetHourType;
  notes: string | null;
  worker?: Worker;
  task?: {
    id: string;
    taskCode: string | null;
    name: string;
    lifecycleStatus: string;
  };
  createdAt: string;
}

export interface Timesheet {
  id: string;
  projectId: string;
  workDate: string;
  teamId: string | null;
  submittedByUserId: string | null;
  status: TimesheetStatus;
  submittedAt: string | null;
  approvedByUserId: string | null;
  approvedAt: string | null;
  entries?: TimesheetEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface TimesheetEntryPayload {
  workerId: string;
  taskId?: string | null;
  hours: number;
  hourType?: TimesheetHourType;
  notes?: string | null;
  clientUuid?: string | null;
}

export interface TimesheetPayload {
  projectId: string;
  workDate: string;
  teamId: string;
  clientUuid?: string | null;
  entries?: TimesheetEntryPayload[];
}

export interface WorkerPayload {
  name: string;
  badgeCode?: string | null;
  teamId?: string | null;
  trade?: string | null;
  isActive?: boolean;
}

const unwrap = <T>(response: any): T => {
  const body = response?.data ?? response;
  return Object.prototype.hasOwnProperty.call(body, 'data') ? body.data as T : body as T;
};

class TimesheetService {
  async list(params: { projectId?: string; teamId?: string; status?: TimesheetStatus | ''; from?: string; to?: string } = {}): Promise<Timesheet[]> {
    return unwrap<Timesheet[]>(await apiClient.get('/timesheets', { params }));
  }

  async get(id: string): Promise<Timesheet> {
    return unwrap<Timesheet>(await apiClient.get(`/timesheets/${id}`));
  }

  async create(payload: TimesheetPayload): Promise<Timesheet> {
    return unwrap<Timesheet>(await apiClient.post('/timesheets', payload));
  }

  async submit(id: string): Promise<Timesheet> {
    return unwrap<Timesheet>(await apiClient.post(`/timesheets/${id}/submit`, {}));
  }

  async approve(id: string): Promise<Timesheet> {
    return unwrap<Timesheet>(await apiClient.post(`/timesheets/${id}/approve`, {}));
  }

  async return(id: string): Promise<Timesheet> {
    return unwrap<Timesheet>(await apiClient.post(`/timesheets/${id}/return`, {}));
  }

  async listWorkers(params: { teamId?: string; active?: boolean; badgeCode?: string; includeAllTeams?: boolean } = {}): Promise<Worker[]> {
    return unwrap<Worker[]>(await apiClient.get('/workers', { params }));
  }

  async createWorker(payload: WorkerPayload): Promise<Worker> {
    return unwrap<Worker>(await apiClient.post('/workers', payload));
  }
}

export default new TimesheetService();
